/**
 * AgentDetailPage Component
 *
 * Displays detailed information about a specific agent including:
 * - Agent metadata (domain, created_at, projects)
 * - Editable agent_spec markdown
 * - Job history for this agent
 *
 * Route: /agents/:agent_id
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgent } from '../hooks/useAgents';

export const AgentDetailPage: React.FC = () => {
  const { agent_id } = useParams<{ agent_id: string }>();
  const navigate = useNavigate();

  // Fetch agent data
  const { data: agent, isLoading, error, refetch } = useAgent(agent_id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <svg
          className="animate-spin h-8 w-8 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading agent
                </h3>
                <p className="mt-1 text-sm text-red-700">{error.message}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => refetch()}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Go back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Go back"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{agent.domain}</h1>
                <p className="mt-1 text-sm text-gray-500">Agent Details</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* TODO: Replace with AgentDetail component from Task Group 9 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="space-y-6">
            {/* Agent Metadata */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h2>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Agent ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{agent.agent_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Domain</dt>
                  <dd className="mt-1 text-sm text-gray-900">{agent.domain}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(agent.created_at).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(agent.updated_at).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Agent Spec Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Specifications</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <p className="text-sm text-gray-600">
                  {agent.agent_spec || 'No specifications defined yet. Add learnings and instructions for this agent.'}
                </p>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Note: Full AgentDetail component with markdown editor will be available after Task Group 9 is complete.
              </p>
            </div>

            {/* Job History Section Placeholder */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Job History</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <p className="text-sm text-gray-600">
                  Job history table will be available after Task Group 9 components are complete.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
